// frontend/src/pages/PayveFiles.tsx
// Lists all MP4 files whose key starts with "PAYVE", using the prefix search endpoint.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TableSortLabel,
} from '@mui/material';
import {
  GetApp as DownloadIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  VideoFile as VideoIcon,
  OndemandVideo as PayveIcon,
  VideoLibrary as AnalysisIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  searchByPrefix,
  getPresignedDownloadUrl,
  FileMetadata,
  FileType,
  PresignedUrlResponse,
  formatFileSize,
  getFileName,
} from '../services/api';
import { getDisplayFileName } from '../services/FileUtils';
import { recordActivity } from '../services/activityService';
import { enqueueVideoForAnalysis } from '../services/analysisSession';

const rocheBlue = '#0B41CD';

type Order = 'asc' | 'desc';
type SortField = 'name' | 'lastModified' | 'size';

const PayveFiles: React.FC = () => {
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFile, setSelectedFile] = useState<FileMetadata | null>(null);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<SortField>('lastModified');
  const navigate = useNavigate();

  const formatDate = useCallback((dateString: string | null): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const now = new Date();
      const isThisYear = now.getFullYear() === date.getFullYear();
      return isThisYear
        ? `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}`
        : `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}, ${date.getFullYear()}`;
    } catch {
      return 'N/A';
    }
  }, []);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fire all three casing variants in parallel — S3 keys are case-sensitive
      // so "PAYVE", "Payve", and "payve" may each return different files.
      const [upper, title, lower] = await Promise.allSettled([
        searchByPrefix('PAYVE'),
        searchByPrefix('Payve'),
        searchByPrefix('payve'),
      ]);

      const seen = new Set<string>();
      const merged: FileMetadata[] = [];

      for (const result of [upper, title, lower]) {
        if (result.status === 'fulfilled' && Array.isArray(result.value?.files)) {
          for (const f of result.value.files) {
            const basename = f.key.split('/').pop() ?? f.key;
            if (
              basename.toLowerCase().startsWith('payve') &&
              f.key.toLowerCase().endsWith('.mp4') &&
              !seen.has(f.key)
            ) {
              seen.add(f.key);
              merged.push(f);
            }
          }
        }
      }

      setFiles(merged);
    } catch (err: any) {
      const msg = err.message || 'Unknown error';
      setError(
        msg.includes('Network Error')
          ? 'Cannot connect to the server. Please check your connection or try again later.'
          : 'Failed to load PAYVE files. Please try again later.'
      );
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, [retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleRetry = useCallback(() => setRetryCount((n) => n + 1), []);

  const handlePlay = useCallback(
    (file: FileMetadata) => {
      recordActivity(file.key, 'played', FileType.VIDEO);
      navigate(`/play/${encodeURIComponent(file.key)}`);
    },
    [navigate]
  );

  const handleDownload = async (
    event: React.MouseEvent<HTMLLIElement>,
    file: FileMetadata
  ) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      setLoading(true);
      recordActivity(file.key, 'downloaded', FileType.VIDEO);
      const response: PresignedUrlResponse = await getPresignedDownloadUrl(file.key);
      const link = document.createElement('a');
      link.href = response.url;
      link.download = response.filename || getFileName(file.key);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setError('Failed to download file. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      handleMenuClose();
    }
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    file: FileMetadata
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFile(null);
  };

  const handleAddToAnalysis = useCallback(
    (file: FileMetadata) => {
      enqueueVideoForAnalysis(file.key);
      navigate('/analysis');
    },
    [navigate]
  );

  const handleRequestSort = (property: SortField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      if (orderBy === 'name') {
        return (
          getDisplayFileName(a.key).localeCompare(getDisplayFileName(b.key)) *
          (order === 'asc' ? 1 : -1)
        );
      }
      if (orderBy === 'lastModified') {
        const ta = a.lastModified ? new Date(a.lastModified).getTime() : -Infinity;
        const tb = b.lastModified ? new Date(b.lastModified).getTime() : -Infinity;
        return (ta - tb) * (order === 'asc' ? 1 : -1);
      }
      if (orderBy === 'size') {
        return (a.size - b.size) * (order === 'asc' ? 1 : -1);
      }
      return 0;
    });
  }, [files, order, orderBy]);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PayveIcon sx={{ color: rocheBlue, fontSize: 28 }} />
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            PAYVE
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={handleRetry}
          disabled={loading}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>

      <Typography variant="body1" paragraph color="text.secondary">
        All MP4 files with names starting with <strong>PAYVE</strong>.
      </Typography>

      {/* States */}
      {loading && files.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
          <CircularProgress sx={{ color: rocheBlue }} />
        </Box>
      ) : error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleRetry} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Retry'}
            </Button>
          }
        >
          {error}
        </Alert>
      ) : files.length === 0 ? (
        <Alert severity="info">No PAYVE MP4 files found.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <Table sx={{ minWidth: 650 }} aria-label="PAYVE files table">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 400 }}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    File Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>
                  <TableSortLabel
                    active={orderBy === 'lastModified'}
                    direction={orderBy === 'lastModified' ? order : 'asc'}
                    onClick={() => handleRequestSort('lastModified')}
                  >
                    Last Modified
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>
                  <TableSortLabel
                    active={orderBy === 'size'}
                    direction={orderBy === 'size' ? order : 'asc'}
                    onClick={() => handleRequestSort('size')}
                  >
                    Size
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: 80 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedFiles.map((file) => (
                <TableRow
                  key={file.key}
                  hover
                  onClick={() => handlePlay(file)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                >
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <VideoIcon color="primary" sx={{ mr: 1 }} />
                      <Typography sx={{ color: 'text.primary' }}>
                        {getDisplayFileName(file.key)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(file.lastModified)}</TableCell>
                  <TableCell>{formatFileSize(file.size)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, file)}
                      aria-label={`Actions for ${getDisplayFileName(file.key)}`}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Inline loading indicator when refreshing with existing data */}
      {loading && files.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} sx={{ color: rocheBlue }} />
        </Box>
      )}

      {/* Actions menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {selectedFile && (
          <>
            <MenuItem
              onClick={(e) => {
                handlePlay(selectedFile);
                handleMenuClose();
              }}
            >
              <ListItemIcon><PlayIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Play</ListItemText>
            </MenuItem>
            <MenuItem onClick={(e) => handleDownload(e, selectedFile)}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Download</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleAddToAnalysis(selectedFile);
                handleMenuClose();
              }}
            >
              <ListItemIcon><AnalysisIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Add to Analysis</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={error}
      />
    </Box>
  );
};

export default PayveFiles;
